import { useQuery } from '@apollo/client'
import debounce from 'lodash/debounce'
import { FC, useCallback, useMemo } from 'react'
import { BROADCAST_CHANNEL, DEBOUNCE_DELAY } from 'constants/consts'
import { Autosuggest, AutosuggestProps } from 'components/Autosuggest/Autosuggest'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import { FIND_ALL_DENTAL_SCHOOLS } from 'queries/dentalSchools'
import {
  findAllDentalSchools,
  findAllDentalSchoolsVariables,
  findAllDentalSchools_findAllDentalSchool_items as DentalSchool,
} from '__generatedTypes__/findAllDentalSchools'
import { useBroadcastChannel } from 'hooks/useBroadcastChannel'
import { REFETCH_DENTAL_SCHOOLS_MESSAGE } from 'pagesNew/DentalSchools/Create'
import { SearchTypeEnum } from '__generatedTypes__/globalTypes'
import { DentalSchoolRenderItem } from './DentalSchoolRenderItem'

type Props = {
  platform: Platform
  isOptometry: boolean
  value: DentalSchool[]
  onAdd: (studyGuide: DentalSchool) => void
} & Partial<AutosuggestProps<DentalSchool>>

export const DentalSchoolsSearch: FC<Props> = ({ platform, isOptometry, value, onAdd, ...rest }) => {
  const platformId = platform?.id

  const { data, refetch: refetchDentalSchools } = useQuery<findAllDentalSchools, findAllDentalSchoolsVariables>(
    FIND_ALL_DENTAL_SCHOOLS,
    {
      fetchPolicy: 'network-only',
      variables: {
        query: {
          search: {
            query: '',
            type: SearchTypeEnum.ILike,
          },
          where: {
            platform: {
              id: platformId,
            },
            isOptometry,
          },
        },
      },
    }
  )

  useBroadcastChannel(BROADCAST_CHANNEL, (event) => {
    if (event.data === REFETCH_DENTAL_SCHOOLS_MESSAGE) {
      refetchDentalSchools()
    }
  })

  const handleSearchInputChange = useCallback(
    debounce((searchString: string) => {
      refetchDentalSchools({
        query: {
          search: {
            query: searchString,
            type: SearchTypeEnum.ILike,
          },
          where: {
            platform: {
              id: platformId,
            },
            isOptometry,
          },
        },
      })
    }, DEBOUNCE_DELAY),
    []
  )

  const filteredDentalSchools = useMemo(() => {
    const addedDentalSchoolsIds = value.map(({ id }) => id)

    return (data?.findAllDentalSchool?.items ?? []).filter(({ id }) => !addedDentalSchoolsIds.includes(id))
  }, [value, data])

  return (
    <Autosuggest<DentalSchool>
      items={filteredDentalSchools}
      onInputChange={handleSearchInputChange}
      getItemLabel={(dentalSchool) => dentalSchool.title}
      renderItem={(dentalSchool) => <DentalSchoolRenderItem dentalSchool={dentalSchool} />}
      placeholder={`Search ${isOptometry ? 'Optometry' : 'Dental'} School`}
      searchable
      withoutCheckMark
      fullWidth
      onChange={(value) => onAdd(value[0])}
      value={value}
      {...rest}
    />
  )
}
